import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import type {
  ConfirmDeliveryInput,
  CreatePhaseInput,
  CreateProjectInput,
  CreateSprintInput,
  Deliverable,
  PaginatedResult,
  Phase,
  PlanningDateRange,
  PlanningDateValidation,
  PlanningEvent,
  PlanningView,
  Project,
  ProjectAttachment,
  ProjectEditLog,
  ProjectHubSummary,
  ProjectListFilters,
  ProjectStatusAction,
  ProjectStatusLog,
  ProjectSummary,
  Sprint,
  ContractCount,
  GapDeleteImpactSolution,
  UpdatePhaseInput,
  UpdateProjectInput,
  UpdateSprintInput,
} from '@internal/orquestra-contract';
import type {
  AttachmentUploadProgress,
  OrquestraHealth,
  OrquestraWhoami,
} from './OrquestraApi.types';

/** Erro de requisição ao backend do Orquestra, carregando o status HTTP. */
export class OrquestraRequestError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'OrquestraRequestError';
  }
}

/**
 * Erro específico de upload de anexo quando o backend responde 413
 * (`Payload Too Large`), acima do limite `ATTACHMENT_MAX_BYTES` (RF4.3).
 */
export class AttachmentTooLargeError extends OrquestraRequestError {
  constructor(message = 'Arquivo excede o tamanho máximo permitido.') {
    super(message, 413);
    this.name = 'AttachmentTooLargeError';
  }
}

/**
 * Erro específico de upload de anexo quando o backend responde 400
 * (validação: extensão não permitida, projeto inexistente, etc.).
 */
export class AttachmentValidationError extends OrquestraRequestError {
  constructor(message = 'Anexo inválido.') {
    super(message, 400);
    this.name = 'AttachmentValidationError';
  }
}

/**
 * Erro específico da exclusão de GAP quando o backend responde 409
 * (`GAP_HAS_ORPHAN_SOLUTIONS`, RF3.3): há Soluções que ficariam órfãs
 * caso o GAP seja removido. Carrega a lista de soluções órfãs para o
 * modal exibir de volta ao usuário.
 */
export class GapDeleteConflictError extends OrquestraRequestError {
  constructor(
    message: string,
    public readonly orphanedSolutions: GapDeleteImpactSolution[],
  ) {
    super(message, 409);
    this.name = 'GapDeleteConflictError';
  }
}

/** Monta a query string da listagem a partir dos filtros. */
function buildProjectsQuery(filters: ProjectListFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) {
    params.set(
      'status',
      Array.isArray(filters.status) ? filters.status.join(',') : filters.status,
    );
  }
  if (filters.startDateFrom) params.set('startDateFrom', filters.startDateFrom);
  if (filters.startDateTo) params.set('startDateTo', filters.startDateTo);
  if (filters.isDeleted !== undefined) {
    params.set('isDeleted', String(filters.isDeleted));
  }
  if (filters.skip !== undefined) params.set('skip', String(filters.skip));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.orderBy) params.set('orderBy', filters.orderBy);
  if (filters.orderDir) params.set('orderDir', filters.orderDir);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Client base que conversa com o plugin backend
 * (`plugins/orquestra-backend`), montado em `/api/orquestra/*`. Cobre
 * fundação, Projetos e Planning. Os métodos financeiros (Fase 3) ficam em
 * `OrquestraFinancialClient`, que estende esta classe — split para manter cada
 * arquivo sob o limite de 300 linhas.
 */
export class OrquestraClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    protected readonly fetchApi: FetchApi,
    private readonly identityApi: IdentityApi,
  ) {}

  protected async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('orquestra');
  }

  protected jsonInit(method: string, body?: unknown): RequestInit {
    return {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    };
  }

  protected async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.json())?.error ?? '';
      } catch {
        // corpo sem JSON — mantém detalhe vazio
      }
      throw new OrquestraRequestError(
        detail ||
          `Requisição ao Orquestra falhou: ${path} (${response.status})`,
        response.status,
      );
    }
    return response.json() as Promise<T>;
  }

  async getHealth(): Promise<OrquestraHealth> {
    return this.request<OrquestraHealth>('/health');
  }

  async whoami(): Promise<OrquestraWhoami> {
    return this.request<OrquestraWhoami>('/whoami');
  }

  async listProjects(
    filters: ProjectListFilters = {},
  ): Promise<PaginatedResult<Project>> {
    return this.request<PaginatedResult<Project>>(
      `/projects${buildProjectsQuery(filters)}`,
    );
  }

  async getProject(id: number): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async createProject(input: CreateProjectInput): Promise<Project> {
    return this.request<Project>('/projects', this.jsonInit('POST', input));
  }

  async updateProject(id: number, input: UpdateProjectInput): Promise<Project> {
    return this.request<Project>(
      `/projects/${id}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteProject(
    id: number,
    justification?: string,
  ): Promise<{ action: string; message: string }> {
    return this.request(
      `/projects/${id}`,
      this.jsonInit('DELETE', { justification }),
    );
  }

  async getProjectSummary(): Promise<ProjectSummary> {
    return this.request<ProjectSummary>('/projects/summary');
  }

  async getContractCount(
    contract: string,
    excludeId?: number,
  ): Promise<ContractCount> {
    const params = new URLSearchParams({ contract });
    if (excludeId !== undefined) params.set('excludeId', String(excludeId));
    return this.request<ContractCount>(`/projects/contract-count?${params}`);
  }

  async changeProjectStatus(
    id: number,
    action: ProjectStatusAction,
    justification?: string,
  ): Promise<Project> {
    return this.request<Project>(
      `/projects/${id}/${action}`,
      this.jsonInit('POST', { justification }),
    );
  }

  async getStatusHistory(id: number): Promise<ProjectStatusLog[]> {
    return this.request<ProjectStatusLog[]>(`/projects/${id}/history`);
  }

  async getEditLog(id: number): Promise<ProjectEditLog[]> {
    return this.request<ProjectEditLog[]>(`/projects/${id}/edit-log`);
  }

  async getHubSummary(id: number): Promise<ProjectHubSummary> {
    return this.request<ProjectHubSummary>(`/projects/${id}/hub-summary`);
  }

  async getPlanning(projectId: number): Promise<PlanningView> {
    return this.request<PlanningView>(`/projects/${projectId}/planning`);
  }

  async createPhase(
    projectId: number,
    input: CreatePhaseInput,
  ): Promise<Phase> {
    return this.request<Phase>(
      `/projects/${projectId}/phases`,
      this.jsonInit('POST', input),
    );
  }

  async updatePhase(
    projectId: number,
    phaseId: number,
    input: UpdatePhaseInput,
  ): Promise<Phase> {
    return this.request<Phase>(
      `/projects/${projectId}/phases/${phaseId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deletePhase(
    projectId: number,
    phaseId: number,
    deletionReason: string,
  ): Promise<void> {
    await this.request(
      `/projects/${projectId}/phases/${phaseId}`,
      this.jsonInit('DELETE', { deletionReason }),
    );
  }

  async validatePhaseDates(
    projectId: number,
    phaseId: number,
    range: PlanningDateRange,
  ): Promise<PlanningDateValidation> {
    return this.request<PlanningDateValidation>(
      `/projects/${projectId}/phases/${phaseId}/validate-dates`,
      this.jsonInit('POST', {
        startDate: range.startDate,
        endDate: range.endDate ?? null,
      }),
    );
  }

  async createSprint(
    projectId: number,
    phaseId: number,
    input: CreateSprintInput,
  ): Promise<Sprint> {
    return this.request<Sprint>(
      `/projects/${projectId}/phases/${phaseId}/sprints`,
      this.jsonInit('POST', input),
    );
  }

  async updateSprint(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: UpdateSprintInput,
  ): Promise<Sprint> {
    return this.request<Sprint>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}`,
      this.jsonInit('PUT', input),
    );
  }

  async deleteSprint(
    projectId: number,
    phaseId: number,
    sprintId: number,
    deletionReason: string,
  ): Promise<void> {
    await this.request(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}`,
      this.jsonInit('DELETE', { deletionReason }),
    );
  }

  async confirmDeliverable(
    projectId: number,
    deliverableId: number,
    input: ConfirmDeliveryInput,
  ): Promise<Deliverable> {
    return this.request<Deliverable>(
      `/projects/${projectId}/deliverables/${deliverableId}/confirm`,
      this.jsonInit('PATCH', input),
    );
  }

  async revertDeliverable(
    projectId: number,
    phaseId: number,
    sprintId: number,
    deliverableId: number,
  ): Promise<Deliverable> {
    return this.request<Deliverable>(
      `/projects/${projectId}/phases/${phaseId}/sprints/${sprintId}` +
        `/deliverables/${deliverableId}/revert`,
      this.jsonInit('POST'),
    );
  }

  async toggleEvent(
    projectId: number,
    eventId: number,
  ): Promise<PlanningEvent> {
    return this.request<PlanningEvent>(
      `/projects/${projectId}/events/${eventId}/toggle`,
      this.jsonInit('PATCH'),
    );
  }

  /** Lista os anexos do projeto (RF4.6). */
  async listAttachments(projectId: number): Promise<ProjectAttachment[]> {
    return this.request<ProjectAttachment[]>(
      `/projects/${projectId}/attachments`,
    );
  }

  /**
   * Faz o upload multipart de um anexo emitindo progresso via `XMLHttpRequest`
   * (RF4.7). Mapeia 413 para {@link AttachmentTooLargeError} e 400 para
   * {@link AttachmentValidationError}; demais erros viram
   * {@link OrquestraRequestError}.
   */
  async uploadAttachment(
    projectId: number,
    file: File,
    onProgress?: (progress: AttachmentUploadProgress) => void,
  ): Promise<ProjectAttachment> {
    const [baseUrl, credentials] = await Promise.all([
      this.getBaseUrl(),
      this.identityApi.getCredentials(),
    ]);
    const url = `${baseUrl}/projects/${projectId}/attachments`;
    const form = new FormData();
    form.append('file', file);

    return new Promise<ProjectAttachment>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      if (credentials?.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${credentials.token}`);
      }
      if (onProgress) {
        xhr.upload.onprogress = event => {
          onProgress({
            loaded: event.loaded,
            total: event.lengthComputable ? event.total : file.size,
          });
        };
      }
      xhr.onerror = () =>
        reject(
          new OrquestraRequestError(
            `Falha de rede ao enviar anexo para o projeto ${projectId}.`,
            0,
          ),
        );
      xhr.onload = () => {
        const status = xhr.status;
        if (status >= 200 && status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as ProjectAttachment);
          } catch (err) {
            reject(
              new OrquestraRequestError(
                'Resposta inválida do backend ao enviar anexo.',
                status,
              ),
            );
          }
          return;
        }
        let detail = '';
        try {
          detail =
            (JSON.parse(xhr.responseText) as { error?: string })?.error ?? '';
        } catch {
          // corpo sem JSON — mantém detalhe vazio
        }
        if (status === 413) {
          reject(
            new AttachmentTooLargeError(
              detail || 'Arquivo excede o tamanho máximo permitido.',
            ),
          );
          return;
        }
        if (status === 400) {
          reject(new AttachmentValidationError(detail || 'Anexo inválido.'));
          return;
        }
        reject(
          new OrquestraRequestError(
            detail || `Falha ao enviar anexo (${status}).`,
            status,
          ),
        );
      };
      xhr.send(form);
    });
  }

  /** Remove um anexo do projeto (RF4.11). */
  async deleteAttachment(
    projectId: number,
    attachmentId: number,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${projectId}/attachments/${attachmentId}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.json())?.error ?? '';
      } catch {
        // corpo sem JSON — mantém detalhe vazio
      }
      throw new OrquestraRequestError(
        detail || `Falha ao remover anexo (${response.status}).`,
        response.status,
      );
    }
  }

  /**
   * Compõe a URL absoluta de download do conteúdo do anexo (RF4.9). Opcional
   * `disposition=inline` habilita preview no navegador; padrão é anexo.
   */
  async getAttachmentDownloadUrl(
    projectId: number,
    attachmentId: number,
    disposition?: 'inline' | 'attachment',
  ): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const path = `${baseUrl}/projects/${projectId}/attachments/${attachmentId}/content`;
    return disposition ? `${path}?disposition=${disposition}` : path;
  }
}
